import { styled } from 'styled-components';
import type { FilesUrlBase, ServiceCode } from '@common/enums';
import type { ProcessActivityAsset } from '@common/validation/processActivityAsset';
import type { PlacementLabel } from '@frontend/utils';
import { getPlacementLabelsForService } from '@frontend/utils';
import type { IAsset } from '../interfaces';
import type { File, FilesByPlacement } from './Viewer';
import { RenderAssetThumbnails } from '../pages/episodes/common';

type ViewerThumbnails = {
    files: FilesByPlacement;
    id?: string;
    onThumbnailClick: (selectedFile: IAsset | ProcessActivityAsset) => Promise<void>;
    selectedFile: File | null;
    serviceCode?: ServiceCode;
    urlBase?: FilesUrlBase;
};

export function ViewerThumbnails({
    files,
    id,
    onThumbnailClick,
    selectedFile,
    serviceCode,
    urlBase,
}: ViewerThumbnails) {
    const servicePlacementLabels = serviceCode ? (getPlacementLabelsForService(serviceCode) ?? []) : [];
    const placementKeys = [
        ...new Set([...servicePlacementLabels.map((placement) => placement.key), ...Object.keys(files)]),
    ];
    const thumbNails = placementKeys.map((key) =>
        renderThumbnailWithLabel(key, servicePlacementLabels, files, onThumbnailClick, selectedFile, id, urlBase)
    );

    return <StyledViewerThumbnails>{thumbNails}</StyledViewerThumbnails>;
}

function renderThumbnailWithLabel(
    key: string,
    placementLabels: PlacementLabel[],
    files: FilesByPlacement,
    onThumbnailClick: (selectedFile: IAsset | ProcessActivityAsset) => Promise<void>,
    selectedFile: File | null,
    id?: string,
    urlBase?: FilesUrlBase
) {
    const foundPlacement = placementLabels?.find((placement) => placement.key === key);
    const label = foundPlacement ? foundPlacement.label : null;

    return (
        <div key={key}>
            {label && <h3>{label}</h3>}
            <RenderAssetThumbnails
                assets={files[key] || []}
                hideFilenames={true}
                id={id}
                onClick={onThumbnailClick}
                selectedAssetId={selectedFile?.id || ''}
                urlBase={urlBase}
            />
        </div>
    );
}

const StyledViewerThumbnails = styled.div`
    width: 100%;
    display: flex;
    gap: 8px;
    overflow-x: auto;
    flex-shrink: 0;

    ${({ theme }) => theme.mediaQueries.large} {
        flex-direction: column;
        gap: 16px;
        min-width: 144px;
        width: 144px;
        overflow-y: auto;
        height: 100%;
    }
`;
